import { Fragment } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  getSerpStoreOpenAccounts,
  SERP_STORE_OPEN_ACCOUNT_COLUMNS,
  SERP_STORE_OPEN_SECTIONS,
  type SerpStoreOpenSource,
} from '@/utils/approval/serpStoreOpen';
import { SerpStoreOpenRoot } from './styles';

type TablesProps = {
  source: SerpStoreOpenSource;
  t: TFunction;
};

/** 레거시 #APV_SERP_SHOP_OPEN .form-group 의 표 6개(칸 배치 동일) */
const SerpStoreOpenTables = ({ source, t }: TablesProps) => {
  const accounts = getSerpStoreOpenAccounts(source);

  return (
    <>
      {SERP_STORE_OPEN_SECTIONS.map(({ title, rows }) => (
        <Fragment key={title}>
          <h2>{t(title)}</h2>
          <table className="table table-bordered-horizontal mb-30 text-center">
            <tbody>
              {rows.map((cells, rowIndex) => (
                <tr key={`${title}-${rowIndex}`}>
                  {cells.map((cell, cellIndex) => (
                    <Fragment key={cell?.key ?? `empty-${cellIndex}`}>
                      <th className="bg-light">
                        {cell ? t(cell.label) : null}
                      </th>
                      <td>{cell ? formatSerpText(source[cell.key]) : null}</td>
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Fragment>
      ))}
      <h2>{t('수금계좌정보')}</h2>
      <table className="table table-bordered-horizontal mb-30 text-center">
        <tbody>
          <tr>
            <th className="bg-light">{t('순번')}</th>
            {SERP_STORE_OPEN_ACCOUNT_COLUMNS.map(({ key, label }) => (
              <th key={key} className="bg-light">
                {t(label)}
              </th>
            ))}
          </tr>
          {accounts.map((account, index) => (
            <tr key={`account-${index}`}>
              <td>{index + 1}</td>
              {SERP_STORE_OPEN_ACCOUNT_COLUMNS.map(({ key }) => (
                <td key={key}>{formatSerpText(account[key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SerpStoreOpenTablesView = (props: TablesProps) => (
  <SerpStoreOpenRoot>
    <div className="form-group">
      <SerpStoreOpenTables {...props} />
    </div>
  </SerpStoreOpenRoot>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSerpStoreOpenContentHtml = (
  source: SerpStoreOpenSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpStoreOpenTables source={source} t={t} />
    </div>,
  );

/** 저장 content(레거시 문서 포함) 그대로 */
export const SerpStoreOpenSnapshot = ({ content }: { content: string }) => (
  <SerpStoreOpenRoot>
    <SerpContentSnapshot content={content} />
  </SerpStoreOpenRoot>
);
